import { API_BASE_URL } from './config';
import { escapeHtml, isValidUrl, formatTimeAgo, apiFetch, markLoaded, renderError } from './helpers';
import type { NewsResponse, NewsStory } from './types';

function renderNewsStory(story: NewsStory, showBlowingUp: boolean): string {
    const sourcesText = escapeHtml(story.sources.join(', '));
    const safeTitle = escapeHtml(story.title);
    const linkHtml = story.link && isValidUrl(story.link)
        ? `<a href="${escapeHtml(story.link)}" target="_blank" rel="noopener noreferrer">${safeTitle}</a>`
        : safeTitle;
    const topicBadge = story.topic && story.topic !== 'world'
        ? `<span class="news-topic-badge news-topic-${escapeHtml(story.topic)}">${escapeHtml(story.topic)}</span>`
        : '';
    const blowingUpBadge = showBlowingUp && story.blowingUp
        ? '<span class="news-blowing-up">&#128293; Trending</span>'
        : '';
    const timeAgo = story.publishedAt ? formatTimeAgo(story.publishedAt) : '';
    const latestUpdateHtml = story.latestUpdate
        ? `<div class="news-latest-update"><span class="news-latest-label" ${story.latestUpdateSummary ? `data-tooltip="${escapeHtml(story.latestUpdateSummary)}"` : ''}>Latest:</span> ${escapeHtml(story.latestUpdate)}</div>`
        : '';
    const metaHtml = `<div class="news-story-meta">${blowingUpBadge}${topicBadge}<span class="news-story-sources">Reported by ${story.sourceCount} source${story.sourceCount !== 1 ? 's' : ''}: ${sourcesText}</span>${timeAgo ? `<span class="news-story-time">${timeAgo}</span>` : ''}</div>`;
    return `<div class="news-story">
        <div class="news-story-title">${linkHtml}</div>
        <div class="news-story-summary">${escapeHtml(story.summary)}</div>
        ${latestUpdateHtml}
        ${metaHtml}
    </div>`;
}

export async function fetchNews(): Promise<void> {
    const container = document.getElementById('news-stories');
    if (!container) return;

    try {
        const response = await apiFetch(`${API_BASE_URL}/news`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: NewsResponse = await response.json();

        if (!data.stories || data.stories.length === 0) {
            container.innerHTML = '<p class="news-error">No stories available right now.</p>';
            return;
        }

        const storiesHtml: string = data.stories
            .map((story: NewsStory) => renderNewsStory(story, true))
            .join('');

        let independentHtml = '';
        if (data.independentStories && data.independentStories.length > 0) {
            const indStories = data.independentStories
                .map((story: NewsStory) => renderNewsStory(story, false))
                .join('');
            independentHtml = `<div class="news-independent-section">
                <div class="news-independent-header">Independent Media</div>
                ${indStories}
            </div>`;
        }

        container.innerHTML = storiesHtml + independentHtml;
        markLoaded(container, 'news');
    } catch (error) {
        renderError(container, 'news', 'Could not load news stories.', error);
    }
}
